import {
  type AnchorHTMLAttributes,
  createContext,
  type ReactElement,
  type ReactNode,
  type Ref,
  useContext,
} from 'react';

/**
 * The props an atoms link-like component hands to a {@link RenderLink} renderer.
 * Standard anchor attributes, plus `to` — the navigation target in the shape
 * some routers prefer (e.g. React Router's `<Link to>`). `to` always mirrors
 * `href`, so a renderer may consume whichever its router expects.
 */
type LinkRenderProps = AnchorHTMLAttributes<HTMLAnchorElement> & {
  to?: string;
};

/**
 * Renders the actual anchor for atoms link-like components (Link, `Button` or
 * `Container` with `href`), delegating to whatever router the host app uses.
 *
 * ## Why this is a render *function*, not an injected component
 *
 * The intuitive shape — "inject a component, render `<LinkComponent />`" — is
 * subtly broken. React reconciles by **element type**. When the thing rendered
 * as `<X />` comes from context, its type identity is opaque: neither React nor
 * the React Compiler can prove it is stable across renders. Consequences:
 *
 * - The Compiler bails out of optimizing every atoms component that renders it.
 * - If the injected component's identity ever changes, React unmounts and
 *   remounts the whole link subtree — losing focus, DOM state, and animations.
 *
 * This is precisely what `react-hooks/static-components` flags.
 *
 * Inverting to a render function removes the problem at the root. atoms
 * **calls** `renderLink(props)` and inlines the result; the element that
 * actually enters the tree is the app's `<NextLink />` / `<RouterLink />`,
 * whose type is module-static **in the app's own code**. React reconciles off
 * that stable type, so nothing remounts and the Compiler can optimize. The
 * renderer's own identity never participates in reconciliation, so it is fine
 * for it to be re-created on every render.
 *
 * ## Contract a renderer MUST follow
 *
 * 1. **Be a function that returns an element — never a component invoked as a
 *    function.** atoms calls `renderLink(props)` directly (no JSX), so the
 *    renderer itself must not use hooks. Returning `<SomeLink {...props} />` is
 *    correct (that element gets its own fiber and may use hooks); passing a
 *    hook-using component *as* the renderer is not (its hooks would detach from
 *    the React tree and break). Base UI's `useRender` even warns in dev when a
 *    render prop "looks like a component".
 * 2. **Spread every received prop** onto the anchor — `className`, event
 *    handlers, `aria-*`, `target`/`rel`, `children`, etc. are all passed in.
 * 3. **Forward `ref`** to the underlying DOM anchor; callers depend on it.
 * 4. **Honor `href`/`to`** — both are provided and mirror each other; map them
 *    to whatever prop your router expects.
 *
 * @example
 * ```tsx
 * // React Router:
 * const renderLink: RenderLink = ({ to, href, ref, ...props }) => (
 *   <RouterLink ref={ref} to={to ?? href ?? ''} {...props} />
 * );
 *
 * // Next.js:
 * const renderLink: RenderLink = ({ to, href, ref, ...props }) => (
 *   <NextLink ref={ref} href={href ?? to ?? ''} {...props} />
 * );
 * ```
 */
type RenderLink = (props: LinkRenderProps & { ref?: Ref<HTMLAnchorElement> }) => ReactElement;

/**
 * Default renderer: a plain DOM anchor. It is module-scope and renders only a
 * host element (`<a>`), so it never triggers the dynamic-component concern
 * described on {@link RenderLink}.
 */
const defaultRenderLink: RenderLink = ({ to, href, ref, ...rest }) => (
  // oxlint-disable-next-line anchor-has-content, atoms/no-raw-elements -- default anchor renderer that Link/LinkProvider delegate to; a raw <a> is intended here
  <a ref={ref} href={href ?? to ?? undefined} {...rest} />
);

const LinkRendererContext = createContext<RenderLink>(defaultRenderLink);

/**
 * Returns the active link renderer (see {@link RenderLink} for the contract).
 * **Call** the result — never render it as JSX:
 *
 * ```tsx
 * const renderLink = useLinkRenderer();
 * return renderLink({ ref, href, className, children });
 * ```
 */
export function useLinkRenderer(): RenderLink {
  return useContext(LinkRendererContext);
}

/**
 * Configures how atoms link-like components (Link, `Button` or `Container`
 * with `href`) render their anchor, so they delegate to the host app's router.
 * See {@link RenderLink} for the renderer contract and the rationale for it
 * being a function rather than an injected component.
 *
 * Defaults to a plain `<a>` when no `renderLink` is provided.
 */
export function LinkProvider({
  renderLink,
  children,
}: {
  renderLink?: RenderLink;
  children: ReactNode;
}) {
  return (
    <LinkRendererContext.Provider value={renderLink ?? defaultRenderLink}>
      {children}
    </LinkRendererContext.Provider>
  );
}

export type { LinkRenderProps, RenderLink };
